import { useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Popconfirm, Space } from 'antd';
import { SendOutlined, StopOutlined } from '@ant-design/icons';
import {
  RoyaltyReportStatus,
  type RoyaltyReportDetailOut,
  type RoyaltyReportOut,
} from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';
import { formatPayable } from '@/utils/money';

const T = 'settlement.royalty_reports';

type Transition = 'send' | 'skip';

interface RoyaltyReportActionsProps {
  /**
   * Picked off the schema rather than spelled out, so a backend rename fails
   * the build instead of quietly confirming `undefined zł`.
   */
  record: Pick<
    RoyaltyReportOut,
    'id' | 'status' | 'payable_amount' | 'currency'
  >;
  /** Lets a show page refetch the record the transition just rewrote. */
  onDone?: () => void;
  size?: 'small' | 'middle';
}

/**
 * The two moves the platform can make on a report. Both are one-way — `send`
 * freezes the row and hands it to the publisher, `skip` is terminal — so each
 * asks first. Anything past `draft` has no moves left and renders nothing.
 */
export const RoyaltyReportActions = ({
  record,
  onDone,
  size = 'middle',
}: RoyaltyReportActionsProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation<RoyaltyReportDetailOut>();
  const [pending, setPending] = useState<Transition | null>(null);

  if (record.status !== RoyaltyReportStatus.draft) return null;

  const run = async (transition: Transition) => {
    setPending(transition);
    try {
      await mutateAsync({
        url: `settlement/royalty_reports/${record.id}/${transition}`,
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: t(`${T}.actions.${transition}.success`),
      });
      invalidate({
        resource: 'settlement.royalty_reports',
        invalidates: ['list', 'detail'],
        id: record.id,
      });
      onDone?.();
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description:
          extractBackendMessage(error) ?? t(`${T}.actions.error_fallback`),
      });
    } finally {
      setPending(null);
    }
  };

  return (
    <Space size={4}>
      {/* The invoice amount, because that is the number the publisher sees. */}
      <Popconfirm
        title={t(`${T}.actions.send.confirm`, {
          amount: formatPayable(record.payable_amount, record.currency),
        })}
        description={t(`${T}.actions.send.confirm_description`)}
        okText={t(`${T}.actions.send.button`)}
        cancelText={t('buttons.cancel')}
        onConfirm={() => run('send')}
      >
        <Button
          type="primary"
          size={size}
          icon={<SendOutlined />}
          loading={pending === 'send'}
        >
          {t(`${T}.actions.send.button`)}
        </Button>
      </Popconfirm>
      <Popconfirm
        title={t(`${T}.actions.skip.confirm`)}
        description={t(`${T}.actions.skip.confirm_description`)}
        okText={t(`${T}.actions.skip.button`)}
        cancelText={t('buttons.cancel')}
        onConfirm={() => run('skip')}
      >
        <Button
          size={size}
          icon={<StopOutlined />}
          loading={pending === 'skip'}
        >
          {t(`${T}.actions.skip.button`)}
        </Button>
      </Popconfirm>
    </Space>
  );
};
